import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  SxProps,
  Theme,
  Stack,
  Typography,
  Pagination
} from '@mui/material';
import styles from '../../../index.module.scss';
import './generic.table.scss';

export interface TableColumn {
  id: string;
  label: string;
  minWidth?: number;
  wrap?: boolean;
  maxWidth?: number;
  width?: number | string; // добавь это!
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
}

interface TableProps {
  columns: TableColumn[];
  data: any[] | undefined;
  ariaLabel?: string;
  containerSx?: SxProps<Theme>;
  rowKey?: (row: any) => React.Key;
  renderDetails?: (row: any) => React.ReactNode;
  emptyMessage?: string;
  pagination?: { page: number; pageSize: number; total: number; onPageChange: (page: number) => void };
}

const GenericTable: React.FC<TableProps> = ({
  columns,
  data,
  ariaLabel = 'simple table',
  containerSx,
  rowKey,
  renderDetails,
  emptyMessage,
  pagination
}) => {
  return (
    <>
      <TableContainer
        component={Paper}
        className="custom-scrollbar"
        sx={[
          {
            // borderRadius: '10px', // Скругление углов всей таблицы
            // maxHeight: '70vh',
            marginTop: '3rem',
            overflow: 'auto', // Обрезка контента по скругленным краям
            boxShadow: 'none',
            flexGrow: 1,
            minHeight: 0
          },
          ...(Array.isArray(containerSx) ? containerSx : containerSx ? [containerSx] : [])
        ]}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: 650,
            fontSize: '1rem'
          }}
          aria-label={ariaLabel}
        >
          <TableHead
            sx={{
              // '& .MuiTableRow-head': { // targeting the head row
              //   borderRadius: '10px 10px 0 0', // applying border-radius
              // },
              // targeting the first cell of the head row
              '& .MuiTableCell-head:first-of-type': {
                borderTopLeftRadius: '8px',
                borderEndStartRadius: '8px'
              },
              // targeting the last cell of the head row
              '& .MuiTableCell-head:last-of-type': {
                borderTopRightRadius: '8px',
                borderEndEndRadius: '8px'
              },
              '& .MuiTableCell-head': {
                borderBottom: 'none' // Убираем нижний бордер
              }
            }}
          >
            <TableRow
              sx={{
                backgroundColor: styles.mainGrey, // Затемнение заголовка таблицы
                border: 'none'
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width, // добавь это!
                    whiteSpace: column.wrap === false ? 'nowrap' : undefined,
                    height: '4rem',
                    fontWeight: 300,
                    color: 'white',
                    backgroundColor: styles.mainGrey,
                    fontSize: '1rem'
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((row, index) => (
              <React.Fragment key={rowKey ? rowKey(row) : index}>
                <TableRow hover role="checkbox" tabIndex={-1}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          width: column.width, // добавь это!
                          whiteSpace: column.wrap === false ? 'nowrap' : undefined,
                          wordBreak: column.wrap === undefined ? 'break-word' : 'normal',
                          overflowWrap:
                            column.wrap === true ? 'anywhere' : column.wrap === false ? 'normal' : 'break-word',
                          height: '4rem',
                          border: 'none', // Убираем нижний бордер
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          fontSize: '1rem'
                        }}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {renderDetails?.(row) && (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ p: 0 }}>
                      {renderDetails(row)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {!data?.length && emptyMessage && (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 5, textAlign: 'center' }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ mt: 2, flexShrink: 0 }}
        >
          <Typography variant="body2" color="text.secondary">
            {pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1}–
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
            {' · '}
            {pagination.pageSize} per page
          </Typography>
          <Pagination
            page={pagination.page}
            count={Math.max(1, Math.ceil(pagination.total / pagination.pageSize))}
            showFirstButton
            showLastButton
            onChange={(_, page) => pagination.onPageChange(page)}
          />
        </Stack>
      )}
    </>
  );
};

export default GenericTable;
